import { useStyles } from '@archipro-design/aria';
import * as S from '~/modules/design-board/component/upload/UploadErrorTile.style';
import UploadErrorTile from '~/modules/design-board/component/upload/UploadErrorTile';
import UploadLoadingTile from '~/modules/design-board/component/upload/UploadLoadingTile';
import type { ImageValidationResult } from '~/modules/file/util/validate-images';
import { useFetcher } from '@remix-run/react';
import {
    API_ACTIONS,
    DESIGN_BOARD_REMIX_API,
} from '~/modules/design-board/config';
import { useEffect, useRef, useState } from 'react';
import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { PinTile } from '~/modules/design-board/component/all-pins/PinTileGrid';
import { useMemoizedFn } from 'ahooks';
import { assertErrorResponse } from '~/modules/root/graphql/responses';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useFetcherIsActionReload } from '~/hooks/useFetcherIsActionReload';
import type { DesignBoardActionResponse } from '~/routes/remix-api.design-board.design-board';

interface UploadTileWithFetcherProps {
    boardId: number;
    validationResult: ImageValidationResult;
    onUpdate: (pinId: number, checked: boolean) => void;
    onDelete: (pinId: number) => void;
    onComplete: (message: string, pin?: DetailedPinWithChecked) => void;
}
const UploadTileWithFetcher = ({
    validationResult,
    boardId,
    onUpdate,
    onDelete,
    onComplete,
}: UploadTileWithFetcherProps) => {
    const { css } = useStyles();
    const fetcher = useFetcher<DesignBoardActionResponse>();
    const [uploadErrorMsg, setUploadErrorMsg] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [pinData, setPinData] = useState<DetailedPinWithChecked>();
    const isSubmittedRef = useRef(false);
    const onCompleteMemo = useMemoizedFn(onComplete);

    const selectedFile =
        validationResult.imagesWithPreview?.[0] || validationResult.files?.[0];

    const uploadPreviewId = selectedFile?.name || 0;

    if (!isSubmittedRef.current) {
        if (validationResult.valid) {
            if (selectedFile && boardId) {
                const formData = new FormData();
                formData.set('file', selectedFile);
                formData.set('boardId', boardId.toString());
                formData.set('previewId', uploadPreviewId.toString());
                formData.set('_action', API_ACTIONS.UPLOAD_DESIGN_BOARD_PIN);

                fetcher.submit(formData, {
                    method: 'post',
                    encType: 'multipart/form-data',
                    action: `${DESIGN_BOARD_REMIX_API}?previewId=${uploadPreviewId}`,
                });

                setIsUploading(true);
            } else {
                setUploadErrorMsg('Wrong board id or file.');
                onCompleteMemo(`Wrong file: ${uploadPreviewId}`);
            }
        } else if (validationResult.message) {
            setUploadErrorMsg(validationResult.message);
            setIsUploading(false);
            onCompleteMemo(`${validationResult.message}: ${uploadPreviewId}`);
        }

        isSubmittedRef.current = true;
    }
    const isActionReload = useFetcherIsActionReload(fetcher);
    useEffect(() => {
        if (!fetcher.data) {
            return;
        }

        if (
            'ID' in fetcher.data &&
            isActionReload &&
            fetcher.data?.Title &&
            fetcher.data?.ID
        ) {
            setIsUploading(false);
            const pin: DetailedPinWithChecked = {
                ...fetcher.data,
                checked: false,
            };
            setPinData(pin);
            onCompleteMemo(fetcher.data?.Title, pin);
        }

        if (
            fetcher.data &&
            assertErrorResponse(fetcher.data) &&
            fetcherIsDone(fetcher) &&
            fetcher.data.error &&
            fetcher.data.message
        ) {
            setUploadErrorMsg(fetcher.data.message);
            setIsUploading(false);
            onCompleteMemo(fetcher.data.message);
        }
    }, [fetcher, onCompleteMemo, isActionReload]);

    if (uploadErrorMsg) {
        return (
            <UploadErrorTile
                errorMsg={uploadErrorMsg}
                css={css(S.ErrorMsgTile)}
            />
        );
    } else if (isUploading) {
        return <UploadLoadingTile />;
    } else if (pinData) {
        return (
            <PinTile
                isAllPins={false}
                key={`$upload-${pinData.ID}`}
                pin={pinData}
                isMobile={false}
                onUpdate={onUpdate}
                onDelete={onDelete}
                index={pinData.ID}
            />
        );
    }

    return <div>PreviewId:{uploadPreviewId}</div>;
};

export default UploadTileWithFetcher;
